import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { siteConfig } from '@/mocks/siteConfig';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
  schemaJson?: object;
  children?: React.ReactNode;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath = '/',
  ogType = 'website',
  ogImage,
  schemaJson,
  children,
}: SEOHeadProps) {
  const location = useLocation();
  const fullUrl = `${siteConfig.url}${canonicalPath}`;

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', fullUrl, true);
    setMeta('og:site_name', siteConfig.name, true);
    if (ogImage) {
      setMeta('og:image', ogImage, true);
    }
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = fullUrl;
  }, [title, description, keywords, fullUrl, ogType, ogImage]);

  return (
    <>
      {schemaJson && (
        <script type="application/ld+json">{JSON.stringify(schemaJson)}</script>
      )}
      {children}
    </>
  );
}